// Liberatoria.tsx — pagina /liberatoria: il testo attivo, nome e cognome, «Ho letto e accetto», Firma; poi il PDF.
// Solo per chi ha un account ed è maggiorenne (lo controlla il server). Il link si manda a tester e persone riprese.
// v029: il testo si apre nella lingua dell'app (o del browser) e si può scegliere tra le lingue in cui c'è; chi ha già
// firmato in una lingua vede quella (e non firma una seconda volta in un'altra).
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Faccina } from "../componenti/Faccina.tsx";
import { ErroreRete } from "../servizi/api.ts";
import { useAccount } from "../servizi/account.ts";
import { firmaLiberatoria, leggiLiberatoria, liberatorieMie, ritiraLiberatoria, scaricaPdfLiberatoria, type TestoLiberatoria } from "../servizi/liberatoria.ts";
import type { ControlloTema } from "../temi/useTema.ts";
import { Indietro } from "../componenti/Indietro.tsx";
import { nomeNativo } from "../i18n/index.ts";
import { eRtl } from "../../../condiviso/lingue.ts";

type Firmata = { id: number; il: string; ritirata: string | null };
type Stato =
  | { tipo: "caricamento" }
  | { tipo: "nessuna" }
  | { tipo: "errore" }
  | { tipo: "pronta"; testo: TestoLiberatoria; firmata: Firmata | null; altra: { lingua: string } | null };

export function Liberatoria({ tema, onIndietro, onAccedi }: { tema: ControlloTema; onIndietro: () => void; onAccedi: () => void }) {
  const { t, i18n } = useTranslation();
  const account = useAccount();
  const [stato, setStato] = useState<Stato>({ tipo: "caricamento" });
  const [nome, setNome] = useState("");
  const [accetto, setAccetto] = useState(false);
  const [invio, setInvio] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [giro, setGiro] = useState(0);
  const [ritiro, setRitiro] = useState(false);
  /** v029: la lingua scelta nella pagina (null = quella dell'app, o quella in cui si è già firmato) */
  const [lingua, setLingua] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    void (async () => {
      try {
        let testo = await leggiLiberatoria(lingua ?? i18n.language);
        // Già firmata (questa versione o un'altra attiva, in un'altra lingua)? Allora si offre solo il PDF.
        const mie = account.tipo === "registrato" ? await liberatorieMie().catch(() => []) : [];
        const attive = testo.lingue ?? [{ lingua: testo.lingua, versione: testo.versione }];
        const firmataAttiva = mie.find((m) => attive.some((a) => a.versione === m.versione));
        const linguaFirmata = firmataAttiva ? attive.find((a) => a.versione === firmataAttiva.versione)!.lingua : null;
        // la prima volta si apre la lingua in cui si è firmato
        if (lingua === null && linguaFirmata && linguaFirmata !== testo.lingua) testo = await leggiLiberatoria(linguaFirmata);
        const f = mie.find((m) => m.versione === testo.versione);
        if (!vivo) return;
        setStato({
          tipo: "pronta",
          testo,
          firmata: f ? { id: f.id, il: f.firmata_il, ritirata: f.ritirata_il ?? null } : null,
          altra: !f && linguaFirmata ? { lingua: linguaFirmata } : null,
        });
      } catch (e) {
        if (vivo) setStato(e instanceof ErroreRete && e.stato === 404 ? { tipo: "nessuna" } : { tipo: "errore" });
      }
    })();
    return () => {
      vivo = false;
    };
  }, [account.tipo, giro, lingua, i18n.language]);

  const data = new Intl.DateTimeFormat(i18n.language, { dateStyle: "long", timeStyle: "short" });

  return (
    <div className="pagina pagina-account ld-pagina">
      <header className="ld-barra">
        <Indietro onClick={onIndietro} />
        <span />
      </header>
      <main className="account-centro liberatoria">
        <Faccina tema={tema.tema} dimensione={56} />
        {stato.tipo === "caricamento" && <p aria-busy="true">{t("generale.caricamento")}</p>}
        {stato.tipo === "nessuna" && <p className="nota-centrata">{t("liberatoria.nessuna")}</p>}
        {stato.tipo === "errore" && <p className="admin-errore" role="alert">{t("account.errore_generico")}</p>}
        {stato.tipo === "pronta" && (
          <>
            <h1 className="account-titolo">{stato.testo.titolo}</h1>
            <p className="nota">{t("liberatoria.versione", { versione: stato.testo.versione })}</p>
            {(stato.testo.lingue?.length ?? 0) > 1 && (
              <label className="campo-etichettato liberatoria-lingua">
                <span className="etichetta">{t("liberatoria.lingua_testo")}</span>
                <select
                  className="campo"
                  value={stato.testo.lingua}
                  onChange={(e) => {
                    // un altro testo: si rilegge e si riconferma
                    setErrore(null);
                    setAccetto(false);
                    setLingua(e.target.value);
                  }}
                >
                  {stato.testo
                    .lingue!.map((l) => ({ l: l.lingua, nome: nomeNativo(l.lingua) }))
                    .sort((a, b) => a.nome.localeCompare(b.nome))
                    .map(({ l, nome }) => (
                      <option key={l} value={l} lang={l}>
                        {nome}
                      </option>
                    ))}
                </select>
              </label>
            )}
            <div className="testo-liberatoria" lang={stato.testo.lingua} dir={eRtl(stato.testo.lingua) ? "rtl" : "ltr"} tabIndex={0}>
              {stato.testo.testo}
            </div>
            {stato.altra ? (
              <section className="scheda-account" data-firmata-altra="true">
                <p role="status">{t("liberatoria.firmata_altra", { lingua: nomeNativo(stato.altra.lingua) })}</p>
                <button type="button" className="pulsante pulsante-secondario" onClick={() => setLingua(stato.altra!.lingua)}>
                  {t("liberatoria.vedi_firmata")}
                </button>
              </section>
            ) : stato.firmata ? (
              <section className="scheda-account" data-firmata="true">
                <p role="status">{t("liberatoria.firmata_il", { data: data.format(new Date(stato.firmata.il)) })}</p>
                <button type="button" className="pulsante pulsante-secondario" onClick={() => void scaricaPdfLiberatoria(stato.firmata!.id).catch(() => setErrore("generico"))}>
                  {t("liberatoria.scarica_pdf")}
                </button>
                {/* v021 (scelta 17): il ritiro; la firma e il ritiro restano come prova */}
                {stato.firmata.ritirata ? (
                  <p className="nota" data-ritirata="true">
                    {t("liberatoria.ritirata_il", { data: data.format(new Date(stato.firmata.ritirata)) })}
                  </p>
                ) : ritiro ? (
                  <div className="ld-ritiro">
                    <p className="nota">{t("liberatoria.ritira_testo")}</p>
                    <button
                      type="button"
                      className="pulsante pulsante-pericolo"
                      disabled={invio}
                      onClick={() => {
                        setInvio(true);
                        ritiraLiberatoria(stato.firmata!.id)
                          .then((r) => setStato({ ...stato, firmata: { ...stato.firmata!, ritirata: r.ritirataIl } }))
                          .catch(() => setErrore("generico"))
                          .finally(() => {
                            setInvio(false);
                            setRitiro(false);
                          });
                      }}
                    >
                      {t("liberatoria.ritira_conferma")}
                    </button>
                    <button type="button" className="pulsante" onClick={() => setRitiro(false)}>
                      {t("generale.annulla")}
                    </button>
                  </div>
                ) : (
                  <button type="button" className="pulsante pulsante-piccolo" onClick={() => setRitiro(true)}>
                    {t("liberatoria.ritira")}
                  </button>
                )}
              </section>
            ) : account.tipo !== "registrato" ? (
              <section className="scheda-account">
                <p>{t("liberatoria.serve_account", { eta: stato.testo.etaMinima })}</p>
                <button type="button" className="pulsante pulsante-primario" onClick={onAccedi}>
                  {t("account.accedi")}
                </button>
              </section>
            ) : (
              <form
                className="scheda-account modulo-liberatoria"
                onSubmit={(e) => {
                  e.preventDefault();
                  setInvio(true);
                  setErrore(null);
                  firmaLiberatoria(stato.testo, nome.trim())
                    .then((r) => setStato({ ...stato, firmata: { id: r.id, il: r.firmata_il, ritirata: null } }))
                    .catch((e2: unknown) => {
                      const c = e2 instanceof ErroreRete ? e2.codice : "generico";
                      setErrore(["eta_insufficiente", "testo_cambiato", "offline", "timeout"].includes(c) ? c : "generico");
                      if (c === "testo_cambiato") setGiro((g) => g + 1);
                    })
                    .finally(() => setInvio(false));
                }}
              >
                <label className="campo-etichettato">
                  <span className="etichetta">{t("liberatoria.nome_cognome")}</span>
                  <input className="campo" value={nome} maxLength={200} autoComplete="name" onChange={(e) => setNome(e.target.value)} required minLength={3} />
                </label>
                <label className="interruttore">
                  <input type="checkbox" checked={accetto} onChange={(e) => setAccetto(e.target.checked)} />
                  <span>{t("liberatoria.accetto")}</span>
                </label>
                <button type="submit" className="pulsante pulsante-primario" disabled={invio || !accetto || nome.trim().length < 3}>
                  {t("liberatoria.firma")}
                </button>
                <p className="nota">{t("liberatoria.cosa_salviamo")}</p>
              </form>
            )}
            {errore && (
              <p className="admin-errore" role="alert">
                {errore === "eta_insufficiente"
                  ? t("liberatoria.errore_eta", { eta: stato.testo.etaMinima })
                  : t(`liberatoria.errore_${errore}`, { defaultValue: t(`account.errore_${errore}`, { defaultValue: t("account.errore_generico") }) })}
              </p>
            )}
          </>
        )}
      </main>
    </div>
  );
}
